/**
 * AuthGate — the authentication boundary for the whole app.
 *
 * State machine:
 *  loading           → minimal loading indicator
 *  auth-error        → generic error + login affordance (no internal detail)
 *  unauthenticated   → Login button (triggers PKCE redirect to Zitadel)
 *  authenticated     → fetch GET /me, then render `children(me)`
 *  me 401            → re-login affordance (session expired / token rejected)
 *  me other error    → generic error message
 *
 * Security invariants (ARCHITECTURE §7, carried over from the M5 page):
 *  - Access / refresh / id tokens are NEVER rendered into the DOM or logged.
 *  - The token is captured locally and passed only to the API client (which puts
 *    it in the Authorization header) — never into component state that renders.
 *  - Logout is handled by the caller via signoutRedirect (clears the oidc store).
 */

import type { MeResponse } from "@tjommi/shared";
import React, { useEffect, useState } from "react";
import { useAuth } from "react-oidc-context";

import { UnauthorizedError, fetchMe } from "../api";

type MeState =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "ok"; data: MeResponse }
  | { status: "unauthorized" }
  | { status: "error"; message: string };

/** Wordmark used on the centred auth screens. */
function Wordmark(): React.JSX.Element {
  return (
    <h1>
      deSjeff
      <span className="dot" />
    </h1>
  );
}

export function AuthGate({
  children,
}: {
  children: (me: MeResponse) => React.ReactNode;
}): React.JSX.Element {
  const auth = useAuth();
  const [meState, setMeState] = useState<MeState>({ status: "idle" });

  useEffect(() => {
    if (!auth.isAuthenticated || !auth.user?.access_token) {
      setMeState({ status: "idle" });
      return;
    }

    // Capture the token locally — never expose it through rendered state.
    const token = auth.user.access_token;
    setMeState({ status: "loading" });

    let ignore = false;
    fetchMe(token)
      .then((data) => {
        if (!ignore) setMeState({ status: "ok", data });
      })
      .catch((err: unknown) => {
        if (ignore) return;
        if (err instanceof UnauthorizedError) {
          setMeState({ status: "unauthorized" });
        } else {
          setMeState({
            status: "error",
            message: err instanceof Error ? err.message : "Unexpected error",
          });
        }
      });

    return () => {
      ignore = true;
    };
  }, [auth.isAuthenticated, auth.user?.access_token]);

  // 1. OIDC provider initialising (initial load / callback processing).
  if (auth.isLoading) {
    return (
      <div className="center-screen">
        <p data-testid="loading">Laster …</p>
      </div>
    );
  }

  // 2. OIDC error (provider unreachable, invalid state, …).
  if (auth.error) {
    return (
      <div className="center-screen">
        <section className="auth-card">
          <Wordmark />
          <p data-testid="auth-error">Kunne ikke fullføre innloggingen. Prøv igjen.</p>
          <button
            type="button"
            className="btn btn-primary"
            data-testid="login-button"
            onClick={() => void auth.signinRedirect()}
          >
            Logg inn
          </button>
        </section>
      </div>
    );
  }

  // 3. Unauthenticated.
  if (!auth.isAuthenticated) {
    return (
      <div className="center-screen">
        <section className="auth-card">
          <Wordmark />
          <p>Din personlige, digitale kokebok.</p>
          <button
            type="button"
            className="btn btn-primary"
            data-testid="login-button"
            onClick={() => void auth.signinRedirect()}
          >
            Logg inn
          </button>
        </section>
      </div>
    );
  }

  // 4. Authenticated — /me fetch states.
  if (meState.status === "loading" || meState.status === "idle") {
    return (
      <div className="center-screen">
        <p data-testid="me-loading">Laster profil …</p>
      </div>
    );
  }

  if (meState.status === "unauthorized") {
    return (
      <div className="center-screen">
        <section className="auth-card">
          <h1>Økten er utløpt</h1>
          <p data-testid="unauthorized-message">Din økt er ikke lenger gyldig. Logg inn på nytt.</p>
          <button
            type="button"
            className="btn btn-primary"
            data-testid="relogin-button"
            onClick={() => void auth.signinRedirect()}
          >
            Logg inn igjen
          </button>
        </section>
      </div>
    );
  }

  if (meState.status === "error") {
    return (
      <div className="center-screen">
        <section className="auth-card">
          <h1>Kunne ikke laste profilen</h1>
          <p data-testid="me-error">{meState.message}</p>
        </section>
      </div>
    );
  }

  // 5. Authenticated + /me loaded — hand control to the app.
  return <>{children(meState.data)}</>;
}
